import React from 'react';
import I18n from '@/modules/i18n';
import Text from '@/components/ui/Text';
import PasswordPolicyCheckboxes, { PASSWORD_POLICY_USER_TYPES } from './PasswordPolicyCheckboxes';
import SettingsToggle from './SettingsToggle';
import SessionTimeoutSelect from './SessionTimeoutSelect';

interface SecuritySettingsProps {
  disabled: boolean;
  onPoliciesChange: (policies: string[]) => void;
  onSessionTimeoutChange: (value: number) => void;
  sessionTimeoutInitialValue: string;
  sessionTimeoutOptions: Record<string, number>;
  settings: {
    session_timeout_in: number;
    strict_password_policies: string[];
  };
  showSessionTimeout: boolean;
}

const SecuritySettings = ({
  disabled,
  onPoliciesChange,
  onSessionTimeoutChange,
  sessionTimeoutInitialValue,
  sessionTimeoutOptions,
  settings,
  showSessionTimeout,
}: SecuritySettingsProps) => {
  const policyEnabled = settings.strict_password_policies.length > 0;

  const handlePolicyToggle = (checked: boolean) => {
    const newPolicies = checked ? PASSWORD_POLICY_USER_TYPES.map(t => t.value) : [];
    onPoliciesChange(newPolicies);
  };

  const handleUserTypeChange = (userType: string, checked: boolean) => {
    const updated = checked
      ? [...settings.strict_password_policies, userType]
      : settings.strict_password_policies.filter(t => t !== userType);
    onPoliciesChange(updated);
  };

  return (
    <div>
      <Text className="tw-mb-2" size="xl" tag="h2" variant="label">
        {I18n.t('security')}
      </Text>
      <div className="tw-space-y-2">
        <SettingsToggle
          checked={policyEnabled}
          description={I18n.t('enable_strict_password_policy_description')}
          disabled={disabled}
          id="admin-setting-strict-password-policy"
          label={I18n.t('enable_strict_password_policy')}
          onChange={handlePolicyToggle}
        >
          <div className="tw-mt-2 tw-pl-[1.25em]">
            <PasswordPolicyCheckboxes
              disabled={disabled}
              selectedUserTypes={settings.strict_password_policies}
              onUserTypeChange={handleUserTypeChange}
            />
          </div>
        </SettingsToggle>

        {showSessionTimeout && (
          <SessionTimeoutSelect
            initialValue={sessionTimeoutInitialValue}
            options={sessionTimeoutOptions}
            value={settings.session_timeout_in}
            onChange={onSessionTimeoutChange}
          />
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
